import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { FlaskConical } from "lucide-react";
import { api, errorText } from "@/lib/api";
import { nbaToday } from "@/lib/format";
import { PageTitle, Panel } from "@/components/AppShell";
import { GameTeamPicker } from "@/components/rotation/GameTeamPicker";
import { OutPicker } from "@/components/rotation/OutPicker";
import { OverridePicker } from "@/components/rotation/OverridePicker";
import { Replacements } from "@/components/rotation/Replacements";
import { ComparisonTable } from "@/components/rotation/ComparisonTable";
import { ScenarioList } from "@/components/rotation/ScenarioList";
import { ArenaHero } from "@/components/rotation/ArenaHero";
import { StartersEditor } from "@/components/rotation/StartersEditor";
import { NewModelMinutes } from "@/components/rotation/NewModelMinutes";
import { useSearchParams } from "react-router-dom";
import "@/components/rotation/RotationArena.css";

export default function Rotation() {
  const [params] = useSearchParams();
  const [day, setDay] = useState(params.get("day") || nbaToday());
  const [gameId, setGameId] = useState(params.get("game") || "");
  const [teamId, setTeamId] = useState(params.get("team") || "");
  const [ctx, setCtx] = useState(null);
  const [outIds, setOutIds] = useState([]);
  const [reps, setReps] = useState([]);
  const [result, setResult] = useState(null);
  const [simError, setSimError] = useState("");
  const [reload, setReload] = useState(0);

  useEffect(() => {
    if (!gameId || !teamId) { setCtx(null); return; }
    setCtx(null); setResult(null);
    api.get("/rotation/context", { params: { game_id: gameId, team_id: teamId } })
      .then((r) => { setCtx(r.data); setOutIds(r.data.players.filter((p) => p.injury === "Out").map((p) => p.id)); setReps([]); })
      .catch((e) => toast.error(errorText(e)));
  }, [gameId, teamId]);

  const material = useMemo(() => (ctx?.players || []).some((p) => outIds.includes(p.id) && p.material), [ctx, outIds]);
  const fixed = useMemo(() => Object.fromEntries(reps.filter((r) => r.pid && r.min !== "").map((r) => [r.pid, Number(r.min)])), [reps]);
  const body = useMemo(() => ({ game_id: gameId, team_id: teamId, out_ids: outIds, fixed_minutes: material ? fixed : {} }), [gameId, teamId, outIds, fixed, material]);

  useEffect(() => {
    if (!ctx) return;
    const t = setTimeout(() => {
      api.post("/rotation/simulate", body).then((r) => { setResult(r.data); setSimError(""); }).catch((e) => setSimError(errorText(e)));
    }, 250);
    return () => clearTimeout(t);
  }, [ctx, body]);

  const save = useCallback(async (note) => {
    try {
      await api.post("/rotation/scenarios", { ...body, note });
      toast.success("Scenario saved");
      setReload((n) => n + 1);
    } catch (e) { toast.error(errorText(e)); }
  }, [body]);

  const loadScenario = (s) => {
    setOutIds(s.out_ids);
    setReps(Object.entries(s.fixed_minutes || {}).map(([pid, min]) => ({ pid, min: String(min) })));
  };

  return (
    <div data-testid="rotation-page" className="ra-page">
      {ctx && <ArenaHero ctx={ctx} />}
      <div className="ra-content">
      <PageTitle overline="Admin · experimental · starting five feeds projections" title={<>Rotation <span className="ra-accent">simulator</span></>}>
        <span data-testid="rotation-experimental-badge" className="inline-flex items-center gap-2 rounded-md border border-amber-500/50 bg-amber-500/10 px-3 py-1.5 text-xs font-bold uppercase tracking-widest text-amber-300">
          <FlaskConical className="h-4 w-4" /> Experimental
        </span>
      </PageTitle>
      <GameTeamPicker day={day} setDay={setDay} gameId={gameId} setGameId={setGameId} teamId={teamId} setTeamId={setTeamId} />
      {gameId && teamId && !ctx && <p className="text-slate-500 mt-6" data-testid="rotation-loading">Loading current-season data…</p>}
      {ctx && (
        <div className="space-y-6 mt-6">
          <div className="ra-top grid lg:grid-cols-3 gap-6 items-start">
            <OutPicker ctx={ctx} outIds={outIds} setOutIds={setOutIds} setCtx={setCtx} />
            <Replacements ctx={ctx} outIds={outIds} material={material} reps={reps} setReps={setReps} />
            <ScenarioList gameId={gameId} reload={reload} onLoad={loadScenario} onSave={save} canSave={!!result && !simError} />
          </div>
          <StartersEditor ctx={ctx} outIds={outIds} setCtx={setCtx} />
          <OverridePicker ctx={ctx} setCtx={setCtx} />
          <NewModelMinutes gameId={gameId} teamId={teamId} />
          <Panel className="p-0 overflow-hidden">
            {simError && <p data-testid="rotation-error" className="p-4 text-rose-400 border-b border-slate-800">{simError}</p>}
            {result ? <div className={simError ? "opacity-30 pointer-events-none" : ""}><ComparisonTable ctx={ctx} result={result} /></div> : !simError && <p className="p-8 text-slate-500">Calculating…</p>}
          </Panel>
        </div>
      )}
      </div>
    </div>
  );
}
